import type { PlaybackNote } from "@midi-fantom-utils/midi-util";
import type { MidiOutputPort } from "./midiPort.js";

/**
 * MIDI-движок «железного» вывода: вместо DSP в браузере шлёт NoteOn/NoteOff/
 * ProgramChange/CC в выбранный MIDI-порт (Fantom 06, GM-синтезатор ОС).
 *
 * Тайминг — JS-таймеры (`setTimeout`), а НЕ future-таймстампы Web MIDI:
 * очереди future-сообщений, уже выставленные ОС, нельзя отменить, а таймеры
 * очищаются синхронно на stop/pause/seek. Точность ~10 мс для превью
 * достаточно.
 *
 * `playNote(note, whenSec, durationSec)` — `whenSec`/`durationSec` задаются в
 * шкале `now()` (по умолчанию `performance.now()/1000`), которая должна
 * совпадать с `clock.now()` контроллера воспроизведения.
 */
export type MidiOutputEngine = {
  playNote(note: PlaybackNote, whenSec: number, durationSec: number): void;
  stopAll(): void;
  setInput(input: {
    port: MidiOutputPort;
    notes: ReadonlyArray<PlaybackNote>;
  }): void;
  dispose(): void;
};

export type MidiOutputEngineOptions = {
  port: MidiOutputPort;
  /**
   * Список нот для паритета с `createSoundfontEngine` (контракт сменного
   * движка). В текущей реализации не используется для планирования —
   * программы резолвятся из каждой ноты (`note.program`); зарезервировано
   * под карту GM→тоны (Bank Select) в Фазе 3.
   */
  notes: ReadonlyArray<PlaybackNote>;
  /** Источник времени (секунды), в шкале которого `whenSec`/`durationSec`. */
  now?: () => number;
};

const statusProgramChange = (channel: number): number => 0xc0 | (channel & 0x0f);
const statusNoteOn = (channel: number): number => 0x90 | (channel & 0x0f);
const statusNoteOff = (channel: number): number => 0x80 | (channel & 0x0f);
const statusControlChange = (channel: number): number => 0xb0 | (channel & 0x0f);

const CC_ALL_SOUND_OFF = 0x78; // 120
const CC_ALL_NOTES_OFF = 0x7b; // 123
const CC_SUSTAIN = 0x40; // 64

const clampVelocity = (value: number): number =>
  Math.max(0, Math.min(127, Math.round(value)));

const clampMidi = (value: number): number =>
  Math.max(0, Math.min(127, Math.round(value)));

const defaultNow = (): number =>
  (typeof performance !== "undefined" ? performance.now() : Date.now()) / 1000;

export const createMidiOutputEngine = (
  initial: MidiOutputEngineOptions
): MidiOutputEngine => {
  let port: MidiOutputPort = initial.port;
  // Паритет с SoundfontEngine: notes принимаем и переписываем в setInput,
  // хотя планирование идёт по `note.program` каждой ноты.
  let notes: ReadonlyArray<PlaybackNote> = initial.notes;
  const now = initial.now ?? defaultNow;

  const pendingTimers = new Set<ReturnType<typeof setTimeout>>();
  // Последняя отправленная программа на канал — чтобы не спамить PC на
  // каждую ноту и не переключать тембр в середине фразы.
  const channelPrograms = new Map<number, number>();
  let disposed = false;

  const clearTimers = () => {
    for (const t of pendingTimers) clearTimeout(t);
    pendingTimers.clear();
  };

  // MIDI panic: глушим все 16 каналов (All Sound Off + All Notes Off +
  // снятие sustain). Покрывает звонящие ноты, которые не успели отработать.
  const panic = () => {
    for (let ch = 0; ch < 16; ch += 1) {
      port.send(new Uint8Array([statusControlChange(ch), CC_ALL_SOUND_OFF, 0]));
      port.send(new Uint8Array([statusControlChange(ch), CC_ALL_NOTES_OFF, 0]));
      port.send(new Uint8Array([statusControlChange(ch), CC_SUSTAIN, 0]));
    }
  };

  const schedule = (delayMs: number, fn: () => void) => {
    const id = setTimeout(fn, delayMs);
    pendingTimers.add(id);
  };

  return {
    playNote: (note, whenSec, durationSec) => {
      if (disposed) return;
      const channel = note.channel & 0x0f;
      // Канал 9 — всегда GM drum map (PC=0), как в SoundfontEngine.
      const program = note.isDrum ? 0 : Math.round(note.program);
      const nowSec = now();
      const onDelayMs = Math.max(0, (whenSec - nowSec) * 1000);
      const offDelayMs = Math.max(0, (whenSec + durationSec - nowSec) * 1000);
      const midi = clampMidi(note.midi);
      const velocity = clampVelocity(note.velocity);

      const lastProgram = channelPrograms.get(channel);
      const pcNeeded = lastProgram === undefined || lastProgram !== program;
      if (pcNeeded) channelPrograms.set(channel, program);

      schedule(onDelayMs, () => {
        // PC шлём непосредственно перед NoteOn, в тот же тик таймера —
        // переключение тембра происходит ровно на старте ноты.
        if (pcNeeded) {
          port.send(new Uint8Array([statusProgramChange(channel), program]));
        }
        port.send(new Uint8Array([statusNoteOn(channel), midi, velocity]));
      });
      schedule(offDelayMs, () => {
        port.send(new Uint8Array([statusNoteOff(channel), midi, 0]));
      });
    },
    stopAll: () => {
      if (disposed) return;
      clearTimers();
      channelPrograms.clear();
      panic();
    },
    setInput: (input) => {
      clearTimers();
      channelPrograms.clear();
      if (port !== input.port) {
        // Глушим предыдущий порт: там могли звучать ноты предыдущего файла.
        panic();
        port = input.port;
      }
      notes = input.notes;
    },
    dispose: () => {
      disposed = true;
      clearTimers();
      channelPrograms.clear();
      panic();
    },
  };
};